import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import * as React from 'react';
import { AuthContext, AuthData } from '../../context/auth';
import { ClaimContext, ClaimData } from '../../context/claim';
import { PolicyContext, PolicyData } from '../../context/policy';
import { formatCurrency } from '../../util/util';

interface Props {
	authData: AuthData;
	policyData: PolicyData;
	claimData: ClaimData;
	subClaimSummary: any;
}

const SubclaimSummaryTable = (props: Props) => {
	const { subClaimSummary, policyData } = props;

	const renderRows = () => {
		const {
			expensePayableAmount,
			expenseReserveAmount,
			lossPayableAmount,
			lossReserveAmount,
		} = subClaimSummary;
		const currency =
			policyData && policyData.policy ? policyData.policy.currency : null;

		// all amounts displayed should be positive
		const lossAvailableReserves = Math.abs(parseFloat(lossReserveAmount));
		const lossTotalPayables = Math.abs(parseFloat(lossPayableAmount));
		const lossTotalReserves = Math.abs(
			parseFloat(lossReserveAmount) + parseFloat(lossPayableAmount),
		);

		const expenseAvailableReserves = Math.abs(parseFloat(expenseReserveAmount));
		const expenseTotalPayables = Math.abs(parseFloat(expensePayableAmount));
		const expenseTotalReserves = Math.abs(
			parseFloat(expenseReserveAmount) + parseFloat(expensePayableAmount),
		);

		const totalLossReserves = Math.abs(
			parseFloat(lossReserveAmount) + parseFloat(lossPayableAmount),
		);
		const totalExpenseReserves = Math.abs(
			parseFloat(expenseReserveAmount) + parseFloat(expensePayableAmount),
		);
		const totalReserves = totalLossReserves + totalExpenseReserves;

		return (
			<TableBody>
				<TableRow>
					<TableCell>Loss</TableCell>
					<TableCell align='right'>
						{formatCurrency(lossAvailableReserves, currency)}
					</TableCell>
					<TableCell align='right'>
						{formatCurrency(lossTotalPayables, currency)}
					</TableCell>
					<TableCell align='right'>
						{formatCurrency(lossTotalReserves, currency)}
					</TableCell>
				</TableRow>
				<TableRow>
					<TableCell>Expense</TableCell>
					<TableCell align='right'>
						{formatCurrency(expenseAvailableReserves, currency)}
					</TableCell>
					<TableCell align='right'>
						{formatCurrency(expenseTotalPayables, currency)}
					</TableCell>
					<TableCell align='right'>
						{formatCurrency(expenseTotalReserves, currency)}
					</TableCell>
				</TableRow>
				<TableRow>
					<TableCell style={{ fontWeight: 'bold' }}>Total Incurred</TableCell>
					<TableCell colSpan={2} />
					<TableCell align='right' style={{ fontWeight: 'bold' }}>
						{formatCurrency(totalReserves, currency)}
					</TableCell>
				</TableRow>
			</TableBody>
		);
	};

	return (
		<TableContainer style={{ marginBottom: '20px' }}>
			<Table size='small'>
				<TableHead>
					<TableRow>
						<TableCell />
						<TableCell align='right'>Available Reserve</TableCell>
						<TableCell align='right'>Total Payables</TableCell>
						<TableCell align='right'>Total Reserves</TableCell>
					</TableRow>
				</TableHead>
				{renderRows()}
			</Table>
		</TableContainer>
	);
};

function withContext(Component: any) {
	return (props: Omit<Props, 'authData' | 'claimData' | 'policyData'>) => (
		<AuthContext.Consumer>
			{(authData) => (
				<PolicyContext.Consumer>
					{(policyData) => (
						<ClaimContext.Consumer>
							{(claimData) => (
								<Component
									{...props}
									authData={authData}
									policyData={policyData}
									claimData={claimData}
								/>
							)}
						</ClaimContext.Consumer>
					)}
				</PolicyContext.Consumer>
			)}
		</AuthContext.Consumer>
	);
}

export default withContext(SubclaimSummaryTable);
